import * as React from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useConfirm } from 'material-ui-confirm';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import { Typography, useTheme } from '@mui/material';
import localStoreUtil from '../ApiSetUp/localstore.util';
import { useAppSelector } from '../Redux/app/hooks';
import Assets from '../Component/Common/Assets';

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
    ({ theme, open }) => ({
        flexGrow: 1,
        padding: theme.spacing(1),
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: open ? 0 : 0,
    }),
);

export default function Layout({ children }) {
    // ================ Hooks ================
    const theme = useTheme();
    const location = useLocation();
    const confirmDialogue = useConfirm();
    const navigate = useNavigate();

    // ================ State ================
    const [open, setOpen] = useState(false);

    // ================ Handler ================
    const handleDrawerToggle = () => {
        setOpen((prevOpen) => !prevOpen); // Toggle the state
    };

    const getPermissionList = useAppSelector((state) => state?.auth?.getRolePermission);

    const loggedInUserDetails = localStoreUtil?.get_data("User_Data");
    const userData = loggedInUserDetails ? JSON.parse(loggedInUserDetails) : null;

    const _logOut = () => {
        confirmDialogue({
            title: (
                <Box>
                    <PowerSettingsNewOutlinedIcon style={{ fontSize: '60px', color: theme.palette.primary.main, }} />
                    <Typography fontSize={"24px"} fontWeight={700}>Are you sure you want to logout?</Typography>
                </Box>
            ),
        })
            .then(() => {
                localStoreUtil.remove_all()
                navigate("/login")
            })
            .catch((err) => {
                console.error(err)
            });
    };


    const sidebarList = [
        {
            name: 'Dashboard',
            path: '/',
            icon: <Assets absolutePath={true} src={`/assets/img/Dashboard.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveDashboard.svg`} />,
            permission: true,
        },
        {
            name: 'Vendor Management',
            path: '/vendor-management',
            icon: <Assets absolutePath={true} src={`/assets/img/Vendor.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveVendor.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Vendor Management') || getPermissionList?.viewPermission?.includes('Vendor Management')) ? true : false,
        },
        {
            name: 'Inventory',
            path: '/inventory',
            icon: <Assets absolutePath={true} src={`/assets/img/Inventory.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveInventory.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Inventory Management') || getPermissionList?.viewPermission?.includes('Inventory Management')) ? true : false,
            subData: [
                {
                    name: 'Common',
                    path: '/inventory/common',
                    subData: [
                        { name: "Unit", path: '/inventory/common/unit' },
                        { name: "Mill Name", path: '/inventory/common/company-name' },
                        { name: "Group", path: '/inventory/common/group' },
                        { name: "GSM", path: '/inventory/common/sub-group' },
                        { name: "Size", path: '/inventory/common/size' },
                        { name: "Color", path: '/inventory/common/color' },
                        { name: "Release Status", path: '/inventory/common/release-status' },
                    ]
                },
                {
                    name: 'Type of Paper',
                    path: '/inventory/type-of-paper',
                },
                {
                    name: 'Micron',
                    path: '/inventory/micron',
                },
                {
                    name: 'Chemical Name',
                    path: '/inventory/chemical-name',
                },
                {
                    name: 'Chemical Category',
                    path: '/inventory/chemical-category',
                },
                // {
                //     name: 'Chemical Code',
                //     path: '/inventory/chemical-code',
                // },
                {
                    name: 'Ink',
                    path: '/inventory/pantone-number',
                },
                {
                    name:"Final Product",
                    path:"/inventory/product"
                }

            ]
        },
        {
            name: 'Purchase Order',
            path: '/purchase-order',
            icon: <Assets absolutePath={true} src={`/assets/img/PurchaseOrder.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActivePurchaseOrder.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Purchase Order') || getPermissionList?.viewPermission?.includes('Purchase Order')) ? true : false,
            subData: [
                {
                    name: 'Documents',
                    path: '/purchase-order/po-documents',
                },
            ]
        },
        {
            name: 'Purchase Entry',
            path: '/purchase-entry',
            icon: <Assets absolutePath={true} src={`/assets/img/PurchaseEntry.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActivePurchaseEntry.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Purchase Entry') || getPermissionList?.viewPermission?.includes('Purchase Entry')) ? true : false,
            subData: [
                {
                    name: 'Lot Number List',
                    path: '/purchase-entry/lot-number-list',
                },
                {
                    name: 'GRN Number',
                    path: '/purchase-entry/grn-number',
                },
                {
                    name: 'Production',
                    path: '/purchase-entry/production',
                },
            ]
        },
        {
            name: 'Processing Unit List',
            path: '/processing-unit-list',
            icon: <Assets absolutePath={true} src={`/assets/img/Processing.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveProcessing.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Processing') || getPermissionList?.viewPermission?.includes('Processing')) ? true : false,
        },
        {
            name: 'Invoice',
            path: '/invoice',
            icon: <Assets absolutePath={true} src={`/assets/img/Invoice.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveInvoice.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Invoice') || getPermissionList?.viewPermission?.includes('Invoice')) ? true : false,
        },
        {
            name: 'Documents',
            path: '/documents',
            icon: <Assets absolutePath={true} src={`/assets/img/Document.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveDocument.svg`} />,
            permission: (getPermissionList?.editPermission?.includes('Documents') || getPermissionList?.viewPermission?.includes('Documents')) ? true : false,
        },
        {
            name: 'User & Role',
            path: '/user-role',
            icon: <Assets absolutePath={true} src={`/assets/img/UserRole.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveUserRole.svg`} />,
            permission: userData?.role == "Admin" ? true : false,
        },
        {
            name: 'Account',
            path: '/account',
            icon: <Assets absolutePath={true} src={`/assets/img/Sales.svg`} />,
            activeIcon: <Assets absolutePath={true} src={`/assets/img/ActiveSales.svg`} />,
            permission: userData?.role == "Admin" ? true : false,
            link : true
        },
    ];


    const filteredTabs = sidebarList?.filter((tab) => {
        return tab.permission;
    });

    return (
        location?.pathname === "/login" || location?.pathname === "/forgot-password" || location?.pathname === "/otp-verification" || location?.pathname === "/enter-password" ? (
            <>
                {children}
            </>
        ) : (
            <>
                <Header open={open} handleDrawerToggle={handleDrawerToggle} logOut={_logOut} filteredTabs={filteredTabs} />
                <Sidebar open={open} handleDrawerToggle={handleDrawerToggle} logOut={_logOut} setOpen={setOpen} filteredTabs={filteredTabs} />
                <Main open={open} sx={{ marginTop: '84px', marginLeft: open ? "320px" : "64px", padding: '0px' }}>
                    {children}
                </Main>
            </>
        )
    );
}
